import React from 'react';
import PropTypes from 'prop-types';
import { Bert } from 'meteor/themeteorchef:bert';
import { map, isEmpty } from 'lodash';
import { Form, InputGroup, Col, Row } from 'react-bootstrap';
import methodPromise from '../../../modules/method-promise-wrapper';

import { SYMBOLOGY_TYPES, DEFAULT } from '../../../api/Styles/Styles';
import * as styleOps from '../../../api/Styles/style-operations';

import CategorizedFields from '../CategorizedFields';
import NumericFields from '../NumericFields';
import SingleSymbologyFields from '../SingleSymbologyFields';

const DEFAULT_COLOR = '#ea6512';

class GeoJSONStyleBuilder extends React.Component {
  state = {
    alpha: styleOps.getAlpha(this.props.style) || 0.9,
    borderAlpha: styleOps.getBorderAlpha(this.props.style),
    borderColor: styleOps.getBorderColor(this.props.style),
    color: styleOps.getColor(this.props.style) || '#ea6512',
    dataLimits: styleOps.getDataLimits(this.props.style),
    legend: styleOps.getLegend(this.props.style),
    pointType: this.props.hasTolerance ? '' : styleOps.getPointType(this.props.style),
    svgCategoryList: [],
    svgCategoryListAPI: '',
    symbologyType: styleOps.getSymbologyType(this.props.style) || SYMBOLOGY_TYPES.SINGLE
  };

  async componentDidMount() {
    try {
      const APISettings = await methodPromise('admin.fetchAPISetting');
      if (APISettings.length > 0) {
        this.setState({ svgCategoryListAPI: APISettings[0].svgCategoryListAPI });
      }
    } catch (error) {
      Bert.alert('SVG category url not found', 'warning');
    }
    this.getSVGCategoryList();
  }

  componentDidUpdate(prevProps) {
    const { style } = this.props;
    const { style: prevStyle } = prevProps;
    if (isEmpty(prevStyle) && !isEmpty(style)) this.resetState(style);
  }

  changePointType = event => this.setState({ pointType: event.target.value.trim() });

  changeSimbology = event => {
    const { style } = this.props;
    const newSymbologyType = event.target.value.trim();

    if (newSymbologyType === styleOps.getSymbologyType(style)) {
      this.setState({
        alpha: styleOps.getAlpha(style),
        legend: styleOps.getLegend(style),
        symbologyType: styleOps.getSymbologyType(style)
      });
    } else {
      this.setState({ alpha: 0.9, symbologyType: newSymbologyType });
    }
  };

  getSVGCategoryList = async () => {
    const { svgCategoryListAPI } = this.state;

    try {
      const response = await fetch(
        'https://sd1.ipsgeoprocesing.ipsilum.com/MG/utilities/list_files_folders',
        {
          method: 'POST',
          body: JSON.stringify({
            path: svgCategoryListAPI,
            beyond_this_folder: null,
            only_folders: null,
            only_files: null,
            filter_type_file: null
          }),
          headers: { 'Content-Type': 'application/json' },
          mode: 'cors',
          credentials: 'omit',
          referrerPolicy: 'no-referrer'
        }
      );
      const answer = await response.json();
      this.setState({ svgCategoryList: answer.data });
    } catch (error) {
      Bert.alert('SVG category list not found', 'warning');
    }
  };

  resetState = style =>
    this.setState({
      alpha: styleOps.getAlpha(style) || 0.9,
      borderAlpha: styleOps.getBorderAlpha(style),
      borderColor: styleOps.getBorderColor(style),
      color: styleOps.getColor(style) || '#ea6512',
      dataLimits: styleOps.getDataLimits(style),
      legend: styleOps.getLegend(style),
      pointType: styleOps.getPointType(style),
      symbologyType: styleOps.getSymbologyType(style)
    });

  updateBorderColor = colorStyle =>
    this.setState({ borderAlpha: colorStyle.rgb.a, borderColor: colorStyle.hex });

  updateColor = colorStyle =>
    this.setState({ alpha: colorStyle.rgb.a, color: colorStyle.hex, legend: [] });

  updateDataLimits = dataLimits => this.setState({ dataLimits });

  updateLegend = (legend, alpha) => this.setState({ alpha, legend });

  render() {
    const {
      color,
      alpha,
      symbologyType,
      legend,
      dataLimits,
      borderAlpha,
      borderColor,
      pointType,
      svgCategoryList
    } = this.state;

    const { style, geojsonGeometry, geojsonProperties, geojson, hasTolerance } = this.props;

    const singleSymbologyFieldsProps = {
      alpha,
      borderAlpha,
      borderColor,
      color: color === DEFAULT ? DEFAULT_COLOR : color,
      geojsonGeometry,
      hasTolerance,
      pointType,
      style,
      svgCategoryList,
      symbologyType,
      changePointType: this.changePointType,
      updateBorderColor: this.updateBorderColor,
      updateColor: this.updateColor
    };

    const numericCategorizedProps = {
      alpha,
      borderAlpha,
      borderColor,
      colorRange: styleOps.getColorRange(style),
      dataLimits,
      geojson,
      geojsonGeometry,
      geojsonProperties,
      hasTolerance,
      legend,
      numIntervals: styleOps.getNumIntervals(style),
      pointType,
      propName: styleOps.getStyledProp(style),
      style,
      svgCategoryList,
      symbologyType,
      changePointType: this.changePointType,
      updateBorderColor: this.updateBorderColor,
      updateDataLimits: this.updateDataLimits,
      updateLegend: this.updateLegend
    };

    return (
      <>
        <Row>
          <Col xs={12}>
            <Form.Group>
              {/* <Form.Label>Simbology type</Form.Label> */}
              <InputGroup className="mb-3">
                <InputGroup.Text id="basic-addon1">Simbology type</InputGroup.Text>
                <Form.Control
                  as="select"
                  name="symbologyType"
                  value={symbologyType}
                  onChange={this.changeSimbology}
                >
                  {map(SYMBOLOGY_TYPES.properties, ({ value, humanValue }) => (
                    <option value={value} key={value}>
                      {humanValue}
                    </option>
                  ))}
                </Form.Control>
              </InputGroup>
            </Form.Group>
            {hasTolerance && (
              <Form.Group>
                <Form.Label>Tolerance</Form.Label>
                <Form.Control
                  type="number"
                  name="tolerance"
                  defaultValue={styleOps.getTolerance(style)}
                  min="0"
                />
              </Form.Group>
            )}
          </Col>
          <SingleSymbologyFields {...singleSymbologyFieldsProps} />
          <CategorizedFields {...numericCategorizedProps} />
          <NumericFields {...numericCategorizedProps} />
        </Row>
      </>
    );
  }
}

GeoJSONStyleBuilder.defaultProps = {
  geojson: {},
  geojsonProperties: []
};

GeoJSONStyleBuilder.propTypes = {
  geojson: PropTypes.object,
  geojsonGeometry: PropTypes.string.isRequired,
  geojsonProperties: PropTypes.array,
  hasTolerance: PropTypes.bool.isRequired,
  style: PropTypes.object.isRequired
};

export default GeoJSONStyleBuilder;
